/**
 * FormRenderer Styles
 *
 * Task: T-P4-D2-F4b-5
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Styles for the FormRenderer component including form layout,
 * submit button with loading states, and animations.
 *
 * Uses design system CSS variables from globals.css.
 */

/* ═══════════════════════════════════════════════════════════
   FORM CONTAINER
   ═══════════════════════════════════════════════════════════ */

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.fieldsContainer {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* ═══════════════════════════════════════════════════════════
   SUBMIT BUTTON
   ═══════════════════════════════════════════════════════════ */

.submitBtn {
  width: 100%;
  padding: var(--space-4) var(--space-6);
  min-height: 56px;
  border: none;
  border-radius: var(--radius-lg);
  background: var(--color-accent);
  color: var(--color-text-primary);
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  transition: all 0.2s var(--ease-bounce);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
}

.submitBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Not ready state - form has unfilled required fields */
.submitBtn.notReady {
  filter: brightness(0.5);
}

.submitBtn:not(:disabled):hover {
  background: var(--color-accent-dark);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.submitBtn:not(:disabled):active {
  transform: scale(0.98);
}

/* Focus state for accessibility */
.submitBtn:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ═══════════════════════════════════════════════════════════
   BUTTON CONTENT
   ═══════════════════════════════════════════════════════════ */

.btnText {
  transition: opacity 0.2s ease;
}

.btnIcon {
  display: flex;
  align-items: center;
  transition: all 0.2s var(--ease-out);
}

.btnIcon svg {
  width: 20px;
  height: 20px;
}

.submitBtn:not(:disabled):hover .btnIcon {
  transform: translateX(4px);
}

/* ═══════════════════════════════════════════════════════════
   LOADING STATE
   ═══════════════════════════════════════════════════════════ */

.submitBtn.loading {
  pointer-events: none;
}

.submitBtn.loading .btnText,
.submitBtn.loading .btnIcon {
  opacity: 0;
}

.btnSpinner {
  position: absolute;
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  opacity: 0;
  animation: none;
}

.submitBtn.loading .btnSpinner {
  opacity: 1;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ═══════════════════════════════════════════════════════════
   SHIMMER EFFECT
   ═══════════════════════════════════════════════════════════ */

.submitBtn::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    120deg,
    transparent 0%,
    rgba(255, 255, 255, 0.2) 50%,
    transparent 100%
  );
  transform: translateX(-100%);
}

.submitBtn:not(:disabled):not(.loading):not(.notReady)::after {
  animation: btnShimmer 3s ease-in-out infinite;
}

@keyframes btnShimmer {
  0%,
  100% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(100%);
  }
}

/* Content block — non-interactive informational text */
.contentBlock {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-3) 0;
  animation: fadeIn var(--duration-normal, 0.3s) var(--ease-out) both;
}

.contentHeading {
  font-family: var(--font-card-title, 'EB Garamond', Georgia, serif);
  font-size: 17px;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.contentBody {
  font-family: var(--font-body, 'Outfit', system-ui, sans-serif);
  font-size: 14px;
  color: var(--color-text-muted);
  line-height: 1.6;
  white-space: pre-wrap;
}
